import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithInterceptors } from "../../app/store/baseQueryWithReauth";
import type { CourseType } from "../Course/types";
import type {
	LoginUserRequest,
	LoginUserResponse,
	RegisterUserRequest,
	RegisterUserResponse,
	UserResponse,
} from "./types/userType";

export const usersApi = createApi({
	baseQuery: baseQueryWithInterceptors,
	tagTypes: ["User", "Course"],
	endpoints: (builder) => ({
		registerUser: builder.mutation<RegisterUserResponse, RegisterUserRequest>({
			query: (user) => ({
				url: "/users/register",
				method: "POST",
				body: user,
			}),
		}),
		getUserById: builder.query<UserResponse, string>({
			query: (id) => `/users/${id}`,
			providesTags: (result, error: unknown, id) => {
				if (error) {
					return [{ type: "User", id: id.toString() }];
				}
				return result ? [{ type: "User", id: id.toString() }] : [];
			},
		}),
		getAllUsers: builder.query<
			UserResponse[],
			{ sortField?: string; sortOrder?: string }
		>({
			query: ({ sortField = "name", sortOrder = "asc" }) =>
				`/users?sortField=${sortField}&sortOrder=${sortOrder}`,
			providesTags: () => {
				return [{ type: "User", id: "LIST" }];
			},
		}),
		loginUser: builder.mutation<LoginUserResponse, LoginUserRequest>({
			query: (user) => ({
				url: "/users/login",
				method: "POST",
				body: user,
			}),
		}),
		logoutUser: builder.mutation<void, void>({
			query: () => ({
				url: "/users/logout",
				method: "POST",
			}),
		}),
		verifyUser: builder.query<
			{
				isAuthenticated: boolean;
				user: {
					id: string;
					enrolledCourses: CourseType[];
				};
			},
			void
		>({
			query: () => ({
				url: "/users/verify",
			}),
		}),
		enrollCourse: builder.mutation<
			{
				message: string;
				status: string;
				user: RegisterUserRequest;
			},
			{ courseId: string; userId: string }
		>({
			query: ({ courseId, userId }) => ({
				url: "/users/enroll",
				method: "PATCH",
				body: { courseId, userId },
			}),
			invalidatesTags: (result, error, { userId, courseId }) => {
				if (error) {
					return [{ type: "User", id: userId }];
				}
				if (!result) {
					return [{ type: "User", id: userId }];
				}
				return [
					{ type: "User", id: userId },
					{ type: "Course", id: courseId },
				];
			},
		}),
		unenrollCourse: builder.mutation<
			{
				message: string;
				status: string;
				user: RegisterUserRequest;
			},
			{ courseId: string; userId: string }
		>({
			query: ({ courseId, userId }) => ({
				url: "/users/unenroll",
				method: "PATCH",
				body: { courseId, userId },
			}),
			invalidatesTags: (result, error, { userId, courseId }) => {
				if (error) {
					return [{ type: "User", id: userId }];
				}
				if (!result) {
					return [{ type: "User", id: userId }];
				}
				return [
					{ type: "User", id: userId },
					{ type: "Course", id: courseId },
				];
			},
		}),
		getUserPoints: builder.query<{ points: number }, string>({
			query: (userId) => ({
				url: `/users/${userId}/points`,
				method: "GET",
				providesTags: (
					result: { points: number },
					error: unknown,
					userId: string,
				) => {
					if (error) {
						return [{ type: "Points", id: userId }];
					}
					if (!result) {
						return [{ type: "Points", id: userId }];
					}
					return [{ type: "Points", id: userId }];
				},
			}),
		}),
	}),
});

export const {
	useRegisterUserMutation,
	useLoginUserMutation,
	useVerifyUserQuery,
	useLogoutUserMutation,
	useEnrollCourseMutation,
	useGetUserByIdQuery,
	useUnenrollCourseMutation,
	useGetUserPointsQuery,
	useGetAllUsersQuery,
} = usersApi;
